/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// part-models.js — a 3D model for every part the catalog offers. Each model
// is the desk's own drawing given height: it stands where the 2D part is
// drawn, over the holes its pins resolve to, with the outline the desk view
// draws it with — so the 3D view is the breadboard on screen, stood up, and
// never a second opinion about where anything is.
//
// What a model may borrow, and does:
//   · WHERE the pins are — model/part-geometry.js's `partPinsWorld` and
//     model/breadboard.js's `holePosition`, the same functions the desk and the
//     netlist read; a leg is drawn into the very hole the netlist joins.
//   · OUTLINES the 2D views already state — a chip's body slab (chip-view.js
//     `chipBodyBox`), a DIP switch's taller body, a TO-220's outline, an
//     inductor's size and a resistor's band layout (discrete-view.js). Those
//     are imported, so a retuned outline moves both views at once.
//   · COLOURS — the theme's tokens (scene3d/palette.js).
// What it adds is the third dimension: heights, and the shapes only a side
// view shows (a TO-92 is a D standing up; a resistor network is a SIP).
//
// Collisions are deliberately ignored in this first pass: a part is drawn
// where it sits whatever else is there, so a long jumper can pass through a
// chip, exactly as the flat desk lets a wire cross one.
//
// `modelKind(def)` names the model a catalog def gets, or null; a test holds
// every PALETTE_DEFS entry to having one, so a part added to the catalog
// without a 3D model fails rather than going missing from the view.

import { chipMarking, footprintOffsets, partDef } from "../catalog/index.js";
import { partNumberOf, transistorCase } from "../catalog/discretes.js";
import { hzLabel } from "../catalog/parts.js";
import { loadBadge, relayCoilVolts } from "../catalog/bench-parts.js";
import {
  acBadge,
  adapterBadge,
  transformerBadge,
} from "../catalog/ac-parts.js";
import { holePosition, rotateOffset } from "../model/breadboard.js";
import { packageSpec, seatsWide } from "../model/footprints.js";
import { partPinsWorld } from "../model/part-geometry.js";
import { legsBent } from "../model/free-legs.js";
import { formatOhms } from "../model/ohm-format.js";
import { formatFarads } from "../model/farad-format.js";
import { formatHenries } from "../model/henry-format.js";
import { chipBodyBox, chipBox } from "../components/chip-view.js";
import {
  BRIDGE_BODY,
  DIP_BODY_BOTTOM,
  DIP_BODY_TOP,
  PUSHLOCK_BODY,
  PUSHLOCK_CAP,
  SEVEN_SEG,
  SPEAKER_BODY,
  TACT_PLATE,
  TO220,
  TYPE_LABEL,
  inductorSize,
  resistorBandLayout,
} from "../components/discrete-view.js";
import {
  METER_COMPACT,
  PROBES,
  isCompact,
  meterSize,
} from "../catalog/meter.js";
import {
  METER_BODY,
  bodyOutline,
  jackOf,
  jackWorld,
  probeWorld,
} from "../model/meter-probes.js";
import { add, cross, normalize, scale, sub } from "./mat4.js";
import { cubicPoints, roundedRect } from "./mesh.js";
import { endOf } from "./wire-model.js";
import { mix, namedColor, shade } from "./palette.js";
import {
  BRICK_HEIGHT,
  DESK_Y,
  HOLE_DEPTH,
  ON_FRONT,
  ON_TOP,
  POST_HEIGHT,
  WIRE_RADIUS,
} from "./scene-builder.js";

const UP = Object.freeze([0, 1, 0]);

/** Printing sits a hair proud of the face it is printed on. */
const INK = 0.005;

/** A DIP's plastic: lifted off the board on its legs, 3.3 mm thick. */
const DIP_Y0 = 0.35;
const DIP_Y1 = 1.65;

/**
 * The model a catalog def gets — the same split the desk's views make
 * (chip-view.js for a DIP chip, discrete-view.js's buildDiscreteSvg for the
 * rest, psu-view/clock-view for the bench bricks) — or null for a def no
 * model knows.
 * @param {object|null} def
 * @returns {string|null}
 */
export function modelKind(def) {
  if (!def) return null;
  if (def.kind === "chip") return "chip";
  if (def.kind === "psu") return "psu";
  if (def.kind === "clock") return "clock";
  if (def.kind === "load") return "load";
  if (def.kind === "meter") return "meter";
  if (def.kind === "ac") return "ac";
  if (def.kind === "transformer") return "transformer";
  if (def.kind === "adapter") return "adapter";
  if (def.regulator) return "regulator";
  if (def.contacts) return "relay";
  if (def.speaker) return "speaker";
  if (def.switchBank) return "dip-switch";
  if (def.rotatable) return "span";
  if (def.can) return "can";
  if (def.characterDisplay) return "lcd";
  if (def.transistor) return "transistor";
  if (def.rectifier) return "bridge";
  switch (def.id) {
    case "seg8cc":
    case "seg8ca":
      return "digit";
    case "bar8":
      return "bar";
    case "bar8iso":
      return "bar-array";
    case "seg7cc":
    case "seg7ca":
      return "seven-seg";
    case "rnet9":
      return "rnet";
    case "pot":
      return "pot";
    case "sw-slide":
      return "slide";
    case "sw-push":
    case "sw-toggle":
      return "button";
    case "sw-tact":
      return "tact";
    case "sw-dpdt":
      return "pushlock";
    default:
      return null;
  }
}

/**
 * Add one component's model to the scene. A part that does not resolve (its
 * board gone, its anchor junk) draws nothing, as on the desk.
 * @param {import("./scene-builder.js").SceneBuilder} sb
 * @param {{boards: object[], components: object[]}} doc
 * @param {object} comp
 * @returns {boolean} whether anything was drawn
 */
export function buildPart(sb, doc, comp) {
  const def = partDef(comp.ref);
  const kind = modelKind(def);
  const build = kind && BUILDERS[kind];
  if (!build) return false;
  const params = paramsOf(def, comp);
  const mark = sb.mark();
  const drawn = build(sb, doc, comp, def, params) !== false;
  if (drawn) {
    sb.modelled.set(comp.id, kind);
    sb.plume(comp.id, mark);
  }
  return drawn;
}

/** The part's params as its def reads them — defaults filled in. */
function paramsOf(def, comp) {
  try {
    return def.normalizeParams?.(comp.params ?? {}) ?? comp.params ?? {};
  } catch {
    return comp.params ?? {};
  }
}

// ── Shared helpers ──────────────────────────────────────────────────────────

/** The world position of a seated part's ANCHOR hole as `{x, z}`, or null —
    the origin every footprint part's outline is stated from. Pin-boards never
    turn, so a seated part's local axes are the world's. */
function anchorOf(doc, comp) {
  const board = doc.boards.find((b) => b.id === comp.board);
  if (!board) return null;
  let pos = null;
  try {
    pos = holePosition(board.type, comp.anchor, board.rot ?? 0);
  } catch {
    return null;
  }
  return pos ? { x: board.x + pos.x, z: board.y + pos.y } : null;
}

/** A point in a part's own frame (origin at the anchor hole). */
const at = (o, lx, y, lz) => [o.x + lx, y, o.z + lz];

/** A slab of plastic over the local rectangle, rounded in plan. */
function slab(sb, o, x0, z0, x1, z1, y0, y1, color, r = 0.12) {
  sb.mesh.extrude(
    roundedRect(o.x + x0, o.z + z0, o.x + x1, o.z + z1, r, y0),
    [0, y1 - y0, 0],
    color,
  );
}

/**
 * A part's own frame, turned a half lap about the middle of its row of holes
 * when the part is (`params.rot` 180 on a `reversible` part — the transistors,
 * the trimmer and the relay, drawn turned on the desk too: discrete-view.js's
 * turnHalfLap): the same holes, the body swung round to the other side of the
 * row. `at`/`slab`/`box` take the unturned part's local coordinates; `dir`
 * turns a direction, and `front`/`top` are the label frames for a face that
 * faced the viewer and for the top. A 180° turn about the vertical is a
 * rotation, not a mirror, so outlines keep their winding.
 */
function partFrame(def, params) {
  const turned = params?.rot === 180 && def?.reversible === true;
  const span = footprintOffsets(def, params).at(-1);
  const lx = (x) => (turned ? span - x : x);
  const lz = (z) => (turned ? -z : z);
  const lo = (a, b) => Math.min(a, b);
  const hi = (a, b) => Math.max(a, b);
  return {
    at: (o, x, y, z) => at(o, lx(x), y, lz(z)),
    slab: (sb, o, x0, z0, x1, z1, y0, y1, color, r) =>
      slab(sb, o, lo(lx(x0), lx(x1)), lo(lz(z0), lz(z1)), hi(lx(x0), lx(x1)), hi(lz(z0), lz(z1)), y0, y1, color, r), // prettier-ignore
    box: (sb, o, [x0, y0, z0], [x1, y1, z1], color) =>
      sb.mesh.box(at(o, lo(lx(x0), lx(x1)), y0, lo(lz(z0), lz(z1))), at(o, hi(lx(x0), lx(x1)), y1, hi(lz(z0), lz(z1))), color), // prettier-ignore
    dir: (d) => (turned ? [-d[0], d[1], -d[2]] : d),
    front: turned ? { right: [-1, 0, 0], up: [0, 1, 0] } : ON_FRONT,
    top: turned ? { right: [-1, 0, 0], up: [0, 0, 1] } : ON_TOP,
  };
}

/** A flat rectangle printed on a face at height `y`. */
function printRect(mesh, o, x0, z0, x1, z1, y, color) {
  mesh.quad(
    at(o, x0, y, z0),
    at(o, x1, y, z0),
    at(o, x1, y, z1),
    at(o, x0, y, z1),
    color,
  );
}

/** A straight leg from inside its hole up to `top`. */
function stub(sb, x, z, top) {
  sb.lead([
    [x, -HOLE_DEPTH, z],
    [x, top, z],
  ]);
}

/** Every pin's world position (`{x, y}` — desk coordinates), or []. */
function pinsOf(doc, comp) {
  try {
    return partPinsWorld(doc.boards, comp) ?? [];
  } catch {
    return [];
  }
}

/**
 * A DIP's legs: down into each pin's hole and out under the body to its
 * nearer long edge, the shoulder a real DIP's leg has.
 * @param {number} zNear - the body edge on row e's side (local z)
 * @param {number} zFar - the body edge on row f's side
 */
function dipLegs(sb, o, pins, zNear, zFar, y0) {
  const color = sb.palette.chipLeg;
  for (const pin of pins) {
    const lz = pin.y - o.z;
    const edge = lz < -1.5 ? zFar : zNear;
    const z0 = Math.min(pin.y, o.z + edge);
    const z1 = Math.max(pin.y, o.z + edge);
    sb.mesh.box(
      [pin.x - 0.11, -HOLE_DEPTH, pin.y - 0.05],
      [pin.x + 0.11, y0 + 0.3, pin.y + 0.05],
      color,
    );
    sb.mesh.box(
      [pin.x - 0.14, y0 + 0.18, z0 - 0.05],
      [pin.x + 0.14, y0 + 0.32, z1 + 0.05],
      color,
    );
  }
}

/** An LED colour's lamp colours: lit, unlit, and the desk's burnt smoke. */
function ledColors(palette, name) {
  const c = namedColor(palette, name);
  return {
    on: mix(shade(c, 1.35), [1, 1, 1], 0.08),
    off: shade(c, 0.38),
    burnt: palette.smoke,
  };
}

/** A label's writing directions on a face looking along the horizontal unit
    `facing`, upright. */
function facingLabel(facing) {
  return { right: cross(UP, facing), up: [...UP] };
}

// ── Chips ───────────────────────────────────────────────────────────────────

/** A DIP chip: a black slab across the channel on its legs, the notch and
    pin-1 dot at pin 1's end, the part number on top. Flipped (rot 180) the
    whole package turns half a lap, notch and printing with it — the real part
    seated the other way round. */
function buildChip(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const { halfPins } = packageSpec(def.package);
  const box = chipBodyBox(def.package, comp.anchor, seatsWide(comp.params));
  const x0 = box.minX;
  const x1 = box.minX + box.width;
  const z0 = box.minY;
  const z1 = box.minY + box.height;
  slab(
    sb,
    o,
    x0,
    z0,
    x1,
    z1,
    DIP_Y0,
    DIP_Y1,
    {
      top: shade(p.chipBody, 1.15),
      side: p.chipBody,
    },
    0.06,
  );
  dipLegs(sb, o, pinsOf(doc, comp), z1, z0, DIP_Y0);

  const flipped = params.rot === 180;
  const top = DIP_Y1 + INK;
  const zMid = (z0 + z1) / 2;
  // The notch: a half-moon at pin 1's end.
  const notchX = flipped ? x1 : x0;
  const dir = flipped ? -1 : 1;
  const notch = [];
  for (let i = 0; i <= 10; i++) {
    const a = -Math.PI / 2 + (i / 10) * Math.PI;
    notch.push(
      at(o, notchX + dir * Math.cos(a) * 0.36, top, zMid + Math.sin(a) * 0.36),
    );
  }
  sb.mesh.polygon(notch, p.chipNotch);
  // Pin 1's dot, in its corner.
  const dot = flipped ? [halfPins - 1 - 0.1, z0 + 0.42] : [0.1, z1 - 0.42];
  sb.mesh.disc(at(o, dot[0], top, dot[1]), UP, 0.15, p.chipNotch, 12);

  sb.label({
    text: chipMarking(def),
    center: at(o, (x0 + x1) / 2 + dir * 0.25, top + INK, zMid),
    right: flipped ? [-1, 0, 0] : ON_TOP.right,
    up: flipped ? [0, 0, 1] : ON_TOP.up,
    height: 0.62,
    maxWidth: box.width - 1.3,
    color: p.chipLabel,
    font: "mono",
  });
}

/** A DIP switch bank: a chip-shaped body, taller as the real part is, with a
    slide actuator per switch over its own column — against the ON band when
    closed (discrete-view.js's buildDipSwitchBank, given height). */
function buildDipSwitch(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const { halfPins: n } = packageSpec(def.package);
  const box = chipBox(def.package);
  const x0 = box.minX + 0.1;
  const x1 = box.minX + box.width - 0.1;
  const Y1 = DIP_Y0 + 1.0;
  slab(
    sb,
    o,
    x0,
    DIP_BODY_TOP,
    x1,
    DIP_BODY_BOTTOM,
    DIP_Y0,
    Y1,
    p.partBody,
    0.15,
  );
  dipLegs(sb, o, pinsOf(doc, comp), DIP_BODY_BOTTOM, DIP_BODY_TOP, DIP_Y0);

  // Seated backwards, the whole face turns half a lap about its centre.
  const flipped = params.rot === 180;
  const turn = (x, z) => (flipped ? [n - 1 - x, -3 - z] : [x, z]);
  const rect = (ax, az, bx, bz, y, color) => {
    const [p0x, p0z] = turn(ax, az);
    const [p1x, p1z] = turn(bx, bz);
    printRect(
      sb.mesh,
      o,
      Math.min(p0x, p1x),
      Math.min(p0z, p1z),
      Math.max(p0x, p1x),
      Math.max(p0z, p1z),
      y,
      color,
    );
  };
  const band = 0.58;
  rect(x0, DIP_BODY_TOP, x1, DIP_BODY_TOP + band, Y1 + INK, p.partInset);
  const [lx, lz] = turn(x0 + 0.55, DIP_BODY_TOP + band / 2);
  sb.label({
    text: "ON", // printed on the part, so never translated (as on the desk)
    center: at(o, lx, Y1 + 2 * INK, lz),
    right: flipped ? [-1, 0, 0] : ON_TOP.right,
    up: flipped ? [0, 0, 1] : ON_TOP.up,
    height: 0.4,
    maxWidth: 1,
    color: p.chipLabel,
    font: "mono",
  });
  const slotZ0 = DIP_BODY_TOP + band + 0.1;
  const slotZ1 = DIP_BODY_BOTTOM - 0.1;
  const states = params.states ?? [];
  for (let i = 0; i < n; i++) {
    rect(i - 0.24, slotZ0, i + 0.24, slotZ1, Y1 + INK, p.partInset);
    const on = states[i] === true;
    const nz0 = on ? slotZ0 + 0.06 : slotZ1 - 0.06 - 0.64;
    const [ax, az] = turn(i - 0.18, nz0);
    const [bx, bz] = turn(i + 0.18, nz0 + 0.64);
    sb.mesh.box(
      at(o, Math.min(ax, bx), Y1, Math.min(az, bz)),
      at(o, Math.max(ax, bx), Y1 + 0.32, Math.max(az, bz)),
      on ? p.success : p.partCap,
    );
  }
}

// ── Displays ────────────────────────────────────────────────────────────────

/** A display's nine legs, down from under the block to its row of holes. */
function displayLegs(sb, o, offsets, bodyEdgeZ) {
  for (const dx of offsets) {
    sb.lead([
      at(o, dx, -HOLE_DEPTH, 0),
      at(o, dx, 0.18, 0),
      at(o, dx, 0.18, bodyEdgeZ - 0.3),
      at(o, dx, 0.45, bodyEdgeZ - 0.3),
    ]);
  }
}

/** The seven-segment digit (common cathode or anode — the same block): the
    desk's segment layout (discrete-view.js's buildDigitDisplay) on top of a
    black block, each segment a lamp the simulation lights. */
function buildDigit(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y0 = 0.3;
  const Y1 = 1.7;
  slab(sb, o, -0.45, -7.4, 8.45, -0.6, Y0, Y1, p.chipBody, 0.4);
  displayLegs(sb, o, footprintOffsets(def, params), -0.6);
  const colors = ledColors(p, params.color);
  const y = Y1 + INK;
  // The desk's numbers (buildDigitDisplay): bar thickness, the verticals'
  // offset, the three bar heights, bar lengths.
  const t = 0.5;
  const cx = 4;
  const hw = 1.4;
  const yT = -6.5;
  const yM = -4.05;
  const yB = -1.6;
  const lh = 2.4;
  const lv = 2.1;
  const hSeg = (sx, sz) => {
    const h = t / 2;
    return [
      [sx - lh / 2, sz],
      [sx - lh / 2 + h, sz - h],
      [sx + lh / 2 - h, sz - h],
      [sx + lh / 2, sz],
      [sx + lh / 2 - h, sz + h],
      [sx - lh / 2 + h, sz + h],
    ];
  };
  const vSeg = (sx, sz) => {
    const h = t / 2;
    return [
      [sx, sz - lv / 2],
      [sx + h, sz - lv / 2 + h],
      [sx + h, sz + lv / 2 - h],
      [sx, sz + lv / 2],
      [sx - h, sz + lv / 2 - h],
      [sx - h, sz - lv / 2 + h],
    ];
  };
  const shapes = {
    a: hSeg(cx, yT),
    b: vSeg(cx + hw, (yT + yM) / 2),
    c: vSeg(cx + hw, (yM + yB) / 2),
    d: hSeg(cx, yB),
    e: vSeg(cx - hw, (yM + yB) / 2),
    f: vSeg(cx - hw, (yT + yM) / 2),
    g: hSeg(cx, yM),
  };
  for (const seg of def.segments) {
    const lamp = sb.lamp({
      kind: "segment",
      compId: comp.id,
      seg: seg.id,
      ...colors,
    });
    if (shapes[seg.id]) {
      lamp.polygon(
        shapes[seg.id].map(([x, z]) => at(o, x, y, z)),
        colors.off,
      );
    } else {
      lamp.disc(at(o, cx + hw + 0.9, y, yB), UP, 0.32, colors.off, 14);
    }
  }
}

/** The bar graph (bar8): eight bars over its block, each a lamp. */
function buildBar(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y1 = 1.5;
  // Turned end for end (`rot` 180), as on the desk: the block swings round
  // its middle hole to the other side of the row, bar 1 to the right.
  const f = partFrame(def, params);
  const turned = params?.rot === 180;
  f.slab(sb, o, -0.45, -4.4, 8.45, -0.6, 0.3, Y1, p.chipBody, 0.35);
  for (const dx of footprintOffsets(def, params)) {
    sb.lead([
      f.at(o, dx, -HOLE_DEPTH, 0),
      f.at(o, dx, 0.18, 0),
      f.at(o, dx, 0.18, -0.9),
      f.at(o, dx, 0.45, -0.9),
    ]);
  }
  const colors = ledColors(p, params.color);
  def.segments.forEach((seg, i) => {
    const lamp = sb.lamp({
      kind: "segment",
      compId: comp.id,
      seg: seg.id,
      ...colors,
    });
    const [x0, x1] = turned ? [8 - i - 0.28, 8 - i + 0.28] : [i - 0.28, i + 0.28]; // prettier-ignore
    const [z0, z1] = turned ? [1.0, 4.0] : [-4.0, -1.0];
    printRect(lamp, o, x0, z0, x1, z1, Y1 + INK, colors.off);
  });
}

/** The isolated bar array (bar8iso): a 16-pin DIP across the channel with a
    bar over each column, the same package as bar8, wired one LED per column. */
function buildBarArray(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y1 = DIP_Y0 + 1.1;
  slab(sb, o, -0.5, -3.4, 7.5, 0.4, DIP_Y0, Y1, p.chipBody, 0.3);
  for (const pin of pinsOf(doc, comp)) stub(sb, pin.x, pin.y, DIP_Y0 + 0.05);
  const colors = ledColors(p, params.color);
  def.segments.forEach((seg, c) => {
    const lamp = sb.lamp({
      kind: "segment",
      compId: comp.id,
      seg: seg.id,
      ...colors,
    });
    // Turned a half lap (`rot` 180), bar 1 stands at the far end.
    const x = params?.rot === 180 ? 7 - c : c;
    printRect(lamp, o, x - 0.28, -3.0, x + 0.28, 0, Y1 + INK, colors.off);
  });
  // Pin 1's dot in the corner over it, as the desk draws it — turned with
  // the bars, it is what says a turned one is turned.
  const dot = params?.rot === 180 ? [7.28, -3.18] : [-0.28, 0.18];
  sb.mesh.disc(at(o, dot[0], Y1 + INK, dot[1]), UP, 0.1, p.partInset, 12);
}

/** The 7-segment display (seg7cc/seg7ca): its block over the ten legs it
    straddles the channel on, and the leaning digit on top — the desk's own
    numbers (discrete-view.js's SEVEN_SEG and buildSevenSegment), each segment
    a lamp. Turned end for end (`rot` 180) it stands on its head about the
    middle of its legs, as on the desk. */
function buildSevenSeg(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const turned = params?.rot === 180;
  // A local point (desk-drawing x, y) — turned a half lap about (2, -3).
  const lp = (x, z) => (turned ? [4 - x, -6 - z] : [x, z]);
  const { left, right, top, bottom, digitTop, digitBottom, lean } = SEVEN_SEG;
  const Y1 = DIP_Y0 + 0.8;
  const [ax, az] = lp(left, top);
  const [bx, bz] = lp(right, bottom);
  slab(sb, o, Math.min(ax, bx), Math.min(az, bz), Math.max(ax, bx), Math.max(az, bz), DIP_Y0, Y1, p.chipBody, 0.2); // prettier-ignore
  for (const pin of pinsOf(doc, comp)) stub(sb, pin.x, pin.y, DIP_Y0 + 0.05);
  const colors = ledColors(p, params.color);
  const y = Y1 + INK;
  const t = 0.42;
  const cx = 1.75;
  const hw = 1.2;
  const yT = digitTop + t / 2;
  const yB = digitBottom - t / 2;
  const yM = (yT + yB) / 2;
  const lh = 2.0;
  const lv = yM - yT - 0.12;
  const leanX = (x, z) => x + (digitBottom - z) * lean;
  const hSeg = (sx, sz) => {
    const h = t / 2;
    return [[sx - lh / 2, sz], [sx - lh / 2 + h, sz - h], [sx + lh / 2 - h, sz - h], [sx + lh / 2, sz], [sx + lh / 2 - h, sz + h], [sx - lh / 2 + h, sz + h]]; // prettier-ignore
  };
  const vSeg = (sx, sz) => {
    const h = t / 2;
    return [[sx, sz - lv / 2], [sx + h, sz - lv / 2 + h], [sx + h, sz + lv / 2 - h], [sx, sz + lv / 2], [sx - h, sz + lv / 2 - h], [sx - h, sz - lv / 2 + h]]; // prettier-ignore
  };
  const shapes = {
    a: hSeg(cx, yT),
    b: vSeg(cx + hw, (yT + yM) / 2),
    c: vSeg(cx + hw, (yM + yB) / 2),
    d: hSeg(cx, yB),
    e: vSeg(cx - hw, (yM + yB) / 2),
    f: vSeg(cx - hw, (yT + yM) / 2),
    g: hSeg(cx, yM),
  };
  const point = (x, z) => {
    const [lx, lz] = lp(leanX(x, z), z);
    return at(o, lx, y, lz);
  };
  for (const seg of def.segments) {
    const lamp = sb.lamp({
      kind: "segment",
      compId: comp.id,
      seg: seg.id,
      ...colors,
    });
    // A half lap is a rotation, not a mirror, so the outlines keep winding.
    if (shapes[seg.id]) {
      lamp.polygon(
        shapes[seg.id].map(([x, z]) => point(x, z)),
        colors.off,
      );
    } else {
      const [lx, lz] = lp(cx + hw + 0.75, yB);
      lamp.disc(at(o, lx, y, lz), UP, 0.26, colors.off, 14);
    }
  }
}

/**
 * A character-LCD module: the green PCB raised on its header, the dark bezel,
 * and the glass, which the renderer paints from the running module's
 * framebuffer (`sb.screen`). Every outline is the def's own datasheet data
 * (catalog/parts.js `characterDisplay`), as on the desk.
 */
function buildLcd(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const cd = def.characterDisplay;
  const PCB0 = 3.0;
  const PCB1 = 3.16;
  const BEZEL = 4.3;
  const offsets = footprintOffsets(def, params);
  // The header the module stands on, and its pins.
  slab(
    sb,
    o,
    -0.5,
    -0.5,
    offsets.at(-1) + 0.5,
    0.5,
    2.2,
    PCB0,
    p.chipBody,
    0.05,
  );
  for (const dx of offsets) stub(sb, o.x + dx, o.z, PCB0 + 0.05);
  const b = cd.body;
  slab(
    sb,
    o,
    b.minX,
    b.minY,
    b.minX + b.width,
    b.minY + b.height,
    PCB0,
    PCB1,
    p.lcdPcb,
    0.3,
  );
  const w = cd.window;
  slab(
    sb,
    o,
    w.x,
    w.y,
    w.x + w.width,
    w.y + w.height,
    PCB1,
    BEZEL,
    p.lcdBezel,
    0.15,
  );
  const s = cd.screen;
  const y = BEZEL + INK;
  const color = params.color in p.lcdScreen ? params.color : "green";
  sb.screen({
    compId: comp.id,
    corners: [
      at(o, s.x, y, s.y),
      at(o, s.x + s.width, y, s.y),
      at(o, s.x + s.width, y, s.y + s.height),
      at(o, s.x, y, s.y + s.height),
    ],
    cols: cd.cols,
    rows: cd.rows,
    screen: p.lcdScreen[color],
    dot: p.lcdDot[color],
  });
}

// ── Switches, networks, trimmers ────────────────────────────────────────────

/** The slide switch: a housing over its three holes and a knob at position
    1 or 2. */
function buildSlide(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y1 = 1.35;
  for (const dx of footprintOffsets(def, params)) stub(sb, o.x + dx, o.z, 0.3);
  slab(sb, o, -0.6, -1, 2.6, 1, 0.25, Y1, p.partBody, 0.2);
  printRect(sb.mesh, o, -0.25, -0.35, 2.25, 0.35, Y1 + INK, p.partInset);
  const kx = params.pos === "2" ? 1.15 : -0.15;
  slab(sb, o, kx, -0.45, kx + 1, 0.45, Y1, Y1 + 0.6, p.partAccent, 0.15);
}

/** The push button and the latching toggle button: a square tactile body
    and its round cap — the toggle's down and green while latched on. */
function buildButton(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y1 = 1.3;
  for (const dx of footprintOffsets(def, params)) stub(sb, o.x + dx, o.z, 0.3);
  slab(sb, o, -0.6, -1.3, 2.6, 1.3, 0.3, Y1, p.partBody, 0.25);
  const latched = def.id === "sw-toggle" && params.on === true;
  sb.mesh.cylinder(
    at(o, 1, Y1, 0),
    UP,
    0.72,
    latched ? 0.28 : 0.55,
    latched ? p.success : p.partCap,
    {
      segments: 20,
    },
  );
}

/** The 6 mm tactile button (sw-tact) across the channel: a black base on four
    legs bent out to rows e and f, its steel plate and the round plunger — the
    desk's own sizes (discrete-view.js's TACT_PLATE), centred on the legs. */
function buildTact(sb, doc, comp) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const half = TACT_PLATE / 2;
  const [x0, x1, z0, z1] = [1 - half, 1 + half, -1.5 - half, -1.5 + half];
  const Y0 = 0.3;
  const Y1 = 1.05;
  dipLegs(sb, o, pinsOf(doc, comp), z1, z0, Y0);
  slab(sb, o, x0, z0, x1, z1, Y0, Y1, p.tactPlunger, 0.1);
  slab(sb, o, x0, z0, x1, z1, Y1, Y1 + 0.08, p.tactPlate, 0.08);
  sb.mesh.cylinder(at(o, 1, Y1 + 0.08, -1.5), UP, 0.86, 0.12, p.tactEdge, {
    segments: 24,
  });
  sb.mesh.cylinder(at(o, 1, Y1 + 0.2, -1.5), UP, 0.66, 0.5, p.tactPlunger, {
    segments: 24,
  });
}

/** The 8 mm self-locking DPDT push switch (sw-dpdt): a white housing on six
    legs across the channel and its square blue cap — standing proud while
    released, sunk into the housing while latched (`params.on`). */
function buildPushLock(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const half = PUSHLOCK_BODY / 2;
  const [x0, x1, z0, z1] = [1 - half, 1 + half, -1.5 - half, -1.5 + half];
  const Y0 = 0.3;
  const Y1 = 2.6;
  dipLegs(sb, o, pinsOf(doc, comp), z1, z0, Y0);
  slab(sb, o, x0, z0, x1, z1, Y0, Y1, p.pushlockBody, 0.14);
  const cap = PUSHLOCK_CAP / 2;
  const latched = params.on === true;
  slab(
    sb,
    o,
    1 - cap,
    -1.5 - cap,
    1 + cap,
    -1.5 + cap,
    Y1 - 0.2,
    Y1 + (latched ? 0.25 : 1.2),
    latched ? p.pushlockCapOn : p.pushlockCap,
    0.12,
  );
}

/** The bridge rectifier: a black epoxy block between the rows on four legs
    — the desk's own outline (discrete-view.js's BRIDGE_BODY) — printed with
    its pins' marks and its part number. Turned a half lap, the block turns
    with its pins and the printing with it, as a chip's does. */
function buildBridge(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const hw = BRIDGE_BODY.width / 2;
  const hh = BRIDGE_BODY.height / 2;
  const [x0, x1, z0, z1] = [1 - hw, 1 + hw, -1.5 - hh, -1.5 + hh];
  const Y0 = 0.3;
  const Y1 = 1.35;
  dipLegs(sb, o, pinsOf(doc, comp), z1, z0, Y0);
  slab(sb, o, x0, z0, x1, z1, Y0, Y1, { top: shade(p.chipBody, 1.15), side: p.chipBody }, 0.08); // prettier-ignore
  const flipped = params.rot === 180;
  // A point on the top face, turned with the block.
  const top = (lx, lz) =>
    flipped ? at(o, 2 - lx, Y1 + INK * 2, -3 - lz) : at(o, lx, Y1 + INK * 2, lz); // prettier-ignore
  const facing = {
    right: flipped ? [-1, 0, 0] : ON_TOP.right,
    up: flipped ? [0, 0, 1] : ON_TOP.up,
  };
  const print = (text, lx, lz, height) =>
    sb.label({ text, center: top(lx, lz), ...facing, height, maxWidth: BRIDGE_BODY.width - 0.5, color: p.chipLabel, font: "mono" }); // prettier-ignore
  print("+", 0, z1 - 0.4, 0.5);
  print("−", 2, z1 - 0.4, 0.5);
  print("~", 0, z0 + 0.4, 0.5);
  print("~", 2, z0 + 0.4, 0.5);
  print(partNumberOf(params) ?? "", 1, -1.5, 0.48);
}

/** The bussed resistor network: a 9-pin SIP standing on its row of holes,
    printed with its value, the dot over pin 1 (the common bus) — which is the
    far end when it is turned end-for-end. */
function buildRnet(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y0 = 0.3;
  const Y1 = 2.6;
  for (const dx of footprintOffsets(def, params))
    stub(sb, o.x + dx, o.z, Y0 + 0.05);
  slab(sb, o, -0.45, -0.45, 8.45, 0.45, Y0, Y1, p.partResistor, 0.2);
  const comX = params.rot === 180 ? 8 : 0;
  sb.mesh.disc(
    at(o, comX, Y1 - 0.35, 0.45 + INK),
    [0, 0, 1],
    0.2,
    p.chipBody,
    12,
  );
  sb.label({
    text: formatOhms(params.ohms),
    center: at(o, 4, (Y0 + Y1) / 2 - 0.1, 0.45 + INK),
    ...ON_FRONT,
    height: 0.7,
    maxWidth: 6,
    color: p.chipBody,
  });
}

/** The trimmer (a Bourns 3296): the blue block over its three pins, the
    brass screw at its right-hand end with the slot turned to the position. */
function buildPot(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const f = partFrame(def, params);
  const p = sb.palette;
  const Y1 = 2.1;
  for (const dx of footprintOffsets(def, params)) stub(sb, o.x + dx, o.z, 0.3);
  f.slab(sb, o, 1 - 1.85, -0.75, 1 + 1.85, 0.75, 0.25, Y1, p.trimpot, 0.2);
  const screw = f.at(o, 2.25, Y1, 0);
  sb.mesh.cylinder(screw, UP, 0.42, 0.14, p.screw, { segments: 18 });
  const turn = ((-135 + 2.7 * (params.position ?? 50)) * Math.PI) / 180;
  const along = f.dir([Math.sin(turn), 0, -Math.cos(turn)]);
  sb.mesh.orientedBox(
    add(screw, [0, 0.15, 0]),
    along,
    UP,
    cross(along, UP),
    [0.34, 0.02, 0.05],
    p.screwSlot,
  );
  sb.label({
    text: Number.isFinite(params.ohms) ? formatOhms(params.ohms) : "",
    center: f.at(o, 0.55, Y1 + INK, 0),
    ...f.top,
    height: 0.5,
    maxWidth: 2.2,
    color: p.trimpotText,
  });
}

// ── Transistors ─────────────────────────────────────────────────────────────

/** Its face's printing: the part number, or the type until it has one. */
const transistorText = (def, params) =>
  partNumberOf(params) ?? TYPE_LABEL[def.transistor.type] ?? "";

/** A linear regulator in its TO-220: the transistor's package, its part
    number on the face, turned with it (`partFrame`). No lamp — what it does
    is a reading. */
function buildRegulator(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const f = partFrame(def, params);
  const p = sb.palette;
  const { left, width, back, tab, front } = TO220;
  const mould = back + tab;
  const Y0 = 0.9;
  const Y1 = 4.4;
  for (const dx of [0, 1, 2]) stub(sb, o.x + dx, o.z, Y0 + 0.05);
  f.box(sb, o, [left, Y0, mould], [left + width, Y1, front], p.transistor);
  f.box(sb, o, [left, Y0, back], [left + width, Y1 + 1.8, mould], p.transistorTab); // prettier-ignore
  for (const z of [mould + INK, back - INK]) {
    sb.mesh.disc(f.at(o, 1, Y1 + 1.0, z), f.dir([0, 0, 1]), 0.5, p.partInset, 16); // prettier-ignore
  }
  sb.label({
    text: def.id,
    center: f.at(o, 1, (Y0 + Y1) / 2, front + INK),
    ...f.front,
    height: 0.6,
    maxWidth: width - 0.4,
    color: p.transistorText,
  });
}

/** A relay: the blue cube over its five legs, the same footprint the desk
    draws it with (discrete-view.js's buildRelay — 5.2 × 4.45 pitch, standing
    back over the rows behind its legs), as tall as a Songle SRD (15.5 mm);
    its rating on the top and the front, and a lamp on top lit while it is
    pulled in. */
function buildRelay(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const f = partFrame(def, params);
  const p = sb.palette;
  const Y0 = 0.3;
  const Y1 = 6.1;
  const back = -4;
  const front = 0.45;
  for (const dx of [0, 1, 2, 3, 4]) stub(sb, o.x + dx, o.z, Y0 + 0.05);
  f.slab(sb, o, -0.6, back, 4.6, front, Y0, Y1, p.relay, 0.25);
  const rating = `${relayCoilVolts(params)}V`;
  sb.label({
    text: rating,
    center: f.at(o, 2, Y1 + INK, -2.4),
    ...f.top,
    height: 1.2,
    maxWidth: 4.2,
    color: p.relayText,
  });
  sb.label({
    text: "RELAY",
    center: f.at(o, 2, Y1 + INK, -1.2),
    ...f.top,
    height: 0.6,
    maxWidth: 4.2,
    color: p.relayText,
  });
  sb.label({
    text: rating,
    center: f.at(o, 2, (Y0 + Y1) / 2, front + INK),
    ...f.front,
    height: 1.1,
    maxWidth: 4.6,
    color: p.relayText,
  });
  const lampColors = { on: p.partOn, off: p.partInset };
  const centre = f.at(o, 4.1, Y1 + INK, -3.5);
  sb.lamp({
    kind: "channel",
    compId: comp.id,
    ...lampColors,
    halo: { center: centre, radius: 0.45 },
  }).disc(centre, UP, 0.2, lampColors.off, 12);
}

/** A speaker: the black can over its two legs, the same circle the desk
    draws it with (discrete-view.js's buildSpeaker — 12 mm across, standing
    back over the rows behind its legs), 9.5 mm tall as KiCad's
    Buzzer_12x9.5RM7.6 is; its cone and dust cap on top, its impedance on
    the rim. */
function buildSpeaker(sb, doc, comp, def, params) {
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const p = sb.palette;
  const Y0 = 0.3;
  const Y1 = 3.75;
  const { r } = SPEAKER_BODY;
  // Turned about its + leg (`params.rot`), as on the desk: the legs where
  // they resolve, the body's centre turned with them.
  const c = rotateOffset({ dx: SPEAKER_BODY.x, dy: SPEAKER_BODY.y }, params.rot ?? 0); // prettier-ignore
  const x = c.dx;
  const y = c.dy;
  for (const pin of pinsOf(doc, comp)) stub(sb, pin.x, pin.y, Y0 + 0.05);
  sb.mesh.cylinder(at(o, x, Y0, y), UP, r, Y1 - Y0, p.speaker, {
    segments: 32,
    capColor: p.speaker,
  });
  sb.mesh.disc(at(o, x, Y1 + INK, y), UP, r - 0.66, p.speakerCone, 32);
  sb.mesh.disc(at(o, x, Y1 + 2 * INK, y), UP, 0.55, p.speaker, 20);
  sb.label({
    text: `${params.impedance}Ω`,
    center: at(o, x, Y1 + 2 * INK, y + r - 0.33),
    ...ON_TOP,
    height: 0.4,
    maxWidth: 1.6,
    color: p.speakerText,
  });
  // Its + leg marked on the top, over that leg, as the desk marks it.
  const plus = rotateOffset({ dx: 0.05, dy: -0.15 }, params.rot ?? 0);
  sb.label({
    text: "+",
    center: at(o, plus.dx, Y1 + 2 * INK, plus.dy),
    ...ON_TOP,
    height: 0.5,
    maxWidth: 0.6,
    color: p.speakerText,
  });
}

/** A transistor, in whichever package it is: a TO-92's D standing on its
    three legs, or a TO-220's moulding and metal tab. Either carries a lamp
    the simulation lights while it conducts. */
function buildTransistor(sb, doc, comp, def, params) {
  if (legsBent(comp)) return buildBentTo92(sb, doc, comp, def, params);
  const o = anchorOf(doc, comp);
  if (!o) return false;
  const f = partFrame(def, params);
  const p = sb.palette;
  const lampColors = { on: p.partOn, off: p.partInset };
  if (transistorCase(def, params) === "TO-220") {
    const { left, width, back, tab, front } = TO220;
    const mould = back + tab;
    const Y0 = 0.9;
    const Y1 = 4.4;
    for (const dx of [0, 1, 2]) stub(sb, o.x + dx, o.z, Y0 + 0.05);
    f.box(sb, o, [left, Y0, mould], [left + width, Y1, front], p.transistor);
    f.box(sb, o, [left, Y0, back], [left + width, Y1 + 1.8, mould], p.transistorTab); // prettier-ignore
    for (const z of [mould + INK, back - INK]) {
      sb.mesh.disc(f.at(o, 1, Y1 + 1.0, z), f.dir([0, 0, 1]), 0.5, p.partInset, 16); // prettier-ignore
    }
    sb.label({
      text: transistorText(def, params),
      center: f.at(o, 1, (Y0 + Y1) / 2, front + INK),
      ...f.front,
      height: 0.6,
      maxWidth: width - 0.4,
      color: p.transistorText,
    });
    const centre = f.at(o, 1, Y1 + INK, (mould + front) / 2);
    sb.lamp({
      kind: "channel",
      compId: comp.id,
      ...lampColors,
      halo: { center: centre, radius: 0.45 },
    }).disc(centre, UP, 0.18, lampColors.off, 12);
    return;
  }
  // TO-92: the moulding is the D of a disc — round at the back, its flat face
  // toward the viewer — standing on the legs (discrete-view.js's buildTo92).
  const legs = [0, 1, 2].map((dx) => [
    f.at(o, dx, -HOLE_DEPTH, 0),
    f.at(o, dx, 0.45, 0),
  ]);
  buildTo92(sb, comp, def, params, o, f, legs);
}

/**
 * A TO-92 on BENT legs (model/free-legs.js): the same moulding, centred on the
 * middle of its three legs — turned a half
 * lap when pin 1 is right of pin 3 — and each leg wire from its own hole up
 * and in under it (discrete-view.js's buildLegsSvg, in the round).
 */
function buildBentTo92(sb, doc, comp, def, params) {
  const pins = pinsOf(doc, comp).sort((a, b) => a.pin - b.pin);
  if (pins.length !== 3) return false;
  // Centred on the MIDDLE of its legs, both ways, as on the desk
  // (discrete-view.js's legsBody): the moulding's plan middle sits 0.17 back
  // of its legs' row, mirrored when it is turned.
  const mean = (k) => pins.reduce((sum, p) => sum + p[k], 0) / 3;
  const turned = pins[0].x > pins[2].x;
  const mid = -0.17;
  const o = { x: mean("x") - 1, z: mean("y") - (turned ? -mid : mid) };
  const f = partFrame(def, turned ? { rot: 180 } : {});
  const legs = pins.map((p) => [
    [p.x, -HOLE_DEPTH, p.y],
    [p.x, 0.45, p.y],
  ]);
  buildTo92(sb, comp, def, params, o, f, legs);
}

/** A TO-92's moulding over `o` in frame `f`, its three legs each running from
    its own `legs` path (hole up) in under it. */
function buildTo92(sb, comp, def, params, o, f, legs) {
  const p = sb.palette;
  const lampColors = { on: p.partOn, off: p.partInset };
  const cx = 1;
  const cz = -0.02;
  const r = 0.72;
  const flat = 0.4;
  const Y0 = 1.0;
  const Y1 = 2.8;
  const s = (flat - cz) / r;
  const from = Math.PI - Math.asin(s);
  const to = Math.asin(s) + 2 * Math.PI;
  const outline = [];
  for (let i = 0; i <= 20; i++) {
    const a = from + ((to - from) * i) / 20;
    outline.push(f.at(o, cx + Math.cos(a) * r, Y0, cz + Math.sin(a) * r));
  }
  sb.mesh.extrude(outline, [0, Y1 - Y0, 0], p.transistor, { smooth: true });
  for (const [i, path] of legs.entries()) {
    const lean = (i - 1) * 0.6;
    sb.lead([...path, f.at(o, 1 + lean * 0.6, Y0 + 0.05, 0)]);
  }
  sb.label({
    text: transistorText(def, params),
    center: f.at(o, cx, (Y0 + Y1) / 2 + 0.2, flat + INK),
    ...f.front,
    height: 0.34,
    maxWidth: 1.05,
    color: p.transistorText,
  });
  const centre = f.at(o, cx, Y1 + INK, cz - 0.3);
  sb.lamp({
    kind: "channel",
    compId: comp.id,
    ...lampColors,
    halo: { center: centre, radius: 0.36 },
  }).disc(centre, UP, 0.14, lampColors.off, 12);
}

// ── Oscillator cans ─────────────────────────────────────────────────────────

/** A crystal oscillator: a metal can over its four corner pins, its pin-1
    dot in the corner nearest pin 1 — wherever a quarter-turn has put it. */
function buildCan(sb, doc, comp, def, params) {
  const pins = pinsOf(doc, comp);
  if (pins.length === 0) return false;
  const p = sb.palette;
  const xs = pins.map((q) => q.x);
  const zs = pins.map((q) => q.y);
  const x0 = Math.min(...xs) - 0.5;
  const x1 = Math.max(...xs) + 0.5;
  const z0 = Math.min(...zs) - 0.5;
  const z1 = Math.max(...zs) + 0.5;
  const Y1 = 1.9;
  for (const pin of pins) stub(sb, pin.x, pin.y, 0.35);
  sb.mesh.extrude(
    roundedRect(x0, z0, x1, z1, 0.3, 0.3),
    [0, Y1 - 0.3, 0],
    p.chipLeg,
  );
  sb.mesh.polygon(
    roundedRect(x0 + 0.22, z0 + 0.22, x1 - 0.22, z1 - 0.22, 0.16, Y1 + INK),
    shade(p.chipLeg, 0.88),
  );
  const centre = [(x0 + x1) / 2, Y1, (z0 + z1) / 2];
  const pin1 = [pins[0].x, Y1, pins[0].y];
  const toward = normalize(sub(centre, pin1), [1, 0, 0]);
  sb.mesh.disc(
    add(add(pin1, scale(toward, 0.45)), [0, 2 * INK, 0]),
    UP,
    0.12,
    p.partInset,
    10,
  );
  sb.label({
    text: hzLabel(params.hz), // the desk's badge, printed as on the can
    center: [centre[0], Y1 + 2 * INK, centre[2]],
    height: 0.6,
    maxWidth: x1 - x0 - 0.8,
    color: p.partInset,
  });
}

// ── Two-lead parts (resistors, LEDs, capacitors, diodes, inductors) ─────────

/**
 * A two-lead part, from where its two leads actually land (`partPinsWorld`):
 * lying along its leads or standing between them, at any angle — the
 * footprint form and the bent free-ends form are one model, because both are
 * just two holes.
 */
function buildSpan(sb, doc, comp, def, params) {
  const pins = pinsOf(doc, comp);
  if (pins.length < 2) return false;
  const a = [pins[0].x, 0, pins[0].y];
  const b = [pins[1].x, 0, pins[1].y];
  const u = normalize(sub(b, a), [1, 0, 0]);
  u[1] = 0;
  const n = [-u[2], 0, u[0]];
  const m = scale(add(a, b), 0.5);
  const ctx = { sb, a, b, u, n, m, comp, def, params, p: sb.palette };
  if (def.polarity) return buildLed(ctx);
  if (def.capacitor) {
    return def.capacitor.polarized ? buildElectrolytic(ctx) : buildCeramic(ctx);
  }
  if (def.diode) return buildDiode(ctx);
  if (def.inductor) return buildInductor(ctx);
  return buildResistor(ctx);
}

/** Point `along` the leads from the middle, at height `y`. */
const onAxis = ({ m, u }, along, y) => [
  m[0] + u[0] * along,
  y,
  m[2] + u[2] * along,
];

/** Both leads of a part lying along its leads at height `y`, from each hole
    up and in to the body's ends `reach` either side of the middle. */
function axialLeads(ctx, y, reach) {
  const { sb, a, b } = ctx;
  sb.lead([[a[0], -HOLE_DEPTH, a[2]], [a[0], y, a[2]], onAxis(ctx, -reach, y)]);
  sb.lead([[b[0], -HOLE_DEPTH, b[2]], [b[0], y, b[2]], onAxis(ctx, reach, y)]);
}

/** Both leads of a part standing between its holes, from each hole up and in
    to its base, `reach` either side of the middle at height `y`. */
function standingLeads(ctx, knee, y, reach) {
  const { sb, a, b } = ctx;
  sb.lead([
    [a[0], -HOLE_DEPTH, a[2]],
    [a[0], knee, a[2]],
    onAxis(ctx, -reach, y),
  ]);
  sb.lead([
    [b[0], -HOLE_DEPTH, b[2]],
    [b[0], knee, b[2]],
    onAxis(ctx, reach, y),
  ]);
}

/** The axial resistor: a beige body with bulged ends lying over its leads,
    banded with its value's colour code (discrete-view.js's layout). */
function buildResistor(ctx) {
  const { sb, u, p, params } = ctx;
  const H = 0.75;
  axialLeads(ctx, H, 0.9);
  sb.mesh.cylinder(onAxis(ctx, -1, H), u, 0.33, 2, p.partResistor);
  sb.mesh.cylinder(onAxis(ctx, -1, H), u, 0.42, 0.48, p.partResistor);
  sb.mesh.cylinder(onAxis(ctx, 0.52, H), u, 0.42, 0.48, p.partResistor);
  for (const { color, at: x } of resistorBandLayout(params.ohms)) {
    const r = Math.abs(x) >= 0.5 ? 0.435 : 0.345;
    sb.mesh.cylinder(
      onAxis(ctx, x - 0.065, H),
      u,
      r,
      0.13,
      p.band[color] ?? p.chipBody,
      {
        segments: 16,
      },
    );
  }
}

/** A diode or Zener: a small glass/epoxy body with its band at the cathode,
    pin 2's end. */
function buildDiode(ctx) {
  const { sb, u, p, def } = ctx;
  const H = 0.5;
  const zener = def.diode?.zener === true;
  axialLeads(ctx, H, 0.7);
  sb.mesh.cylinder(
    onAxis(ctx, -0.75, H),
    u,
    0.3,
    1.5,
    zener ? p.zener : p.diode,
  );
  sb.mesh.cylinder(
    onAxis(ctx, 0.38, H),
    u,
    0.315,
    0.22,
    zener ? p.zenerBand : p.diodeBand,
    {
      segments: 16,
    },
  );
}

/** An inductor in either of its looks: a copper-wound toroid standing on edge
    over its leads, or a drum in a black can (its `style`), sized by the holes
    between its leads (discrete-view.js's inductorSize). */
function buildInductor(ctx) {
  const { sb, n, m, p, params } = ctx;
  const size = inductorSize(params);
  if (params.style === "can") {
    const r = size.drum;
    standingLeads(ctx, 0.15, 0.25, r * 0.6);
    const H = 2.2;
    sb.mesh.cylinder([m[0], 0.25, m[2]], UP, r, H, p.inductorDrum, {
      segments: 24,
    });
    if (Number.isFinite(params.henries)) {
      sb.label({
        text: formatHenries(params.henries),
        center: [m[0], 0.25 + H + INK, m[2]],
        height: Math.min(0.55, r * 0.6),
        maxWidth: r * 1.6,
        color: p.inductorText,
      });
    }
    return;
  }
  const minor = size.width / 2;
  const major = size.length / 2 - minor;
  const centre = [m[0], 0.2 + major + minor, m[2]];
  standingLeads(ctx, 0.15, 0.35, major * 0.7);
  sb.mesh.torus(centre, n, major, minor, p.inductorCopper, {
    segments: 36,
    sides: 12,
  });
  // The ferrite showing through the middle of the winding.
  sb.mesh.cylinder(
    add(centre, scale(n, -minor * 0.6)),
    n,
    major - minor * 0.9,
    minor * 1.2,
    p.inductorCore,
    {
      segments: 24,
    },
  );
}

/** An LED standing on its two legs: flange, body and lens — all one lamp,
    lit in its colour while the simulation drives it, smoke once it burns. */
function buildLed(ctx) {
  const { sb, comp, params, m, p } = ctx;
  standingLeads(ctx, 0.5, 1.25, 0.25);
  const colors = ledColors(p, params.color);
  // The halo stands round the lens (flange at 1.25, dome top at 3.71).
  const lamp = sb.lamp({
    kind: "led",
    compId: comp.id,
    ...colors,
    halo: { center: [m[0], 2.6, m[2]], radius: 1.7 },
  });
  lamp.cylinder([m[0], 1.25, m[2]], UP, 0.8, 0.16, colors.off, {
    segments: 20,
  });
  lamp.cylinder([m[0], 1.41, m[2]], UP, 0.7, 1.6, colors.off, {
    segments: 20,
    caps: false,
  });
  lamp.dome([m[0], 3.01, m[2]], UP, 0.7, colors.off, {
    segments: 20,
    rings: 6,
  });
}

/** A ceramic disc standing on its legs, its value printed on the face that
    looks toward the viewer. */
function buildCeramic(ctx) {
  const { sb, n, m, p, params } = ctx;
  const r = 0.78;
  const centre = [m[0], 0.55 + r, m[2]];
  standingLeads(ctx, 0.35, centre[1] - 0.55, 0.3);
  sb.mesh.cylinder(add(centre, scale(n, -0.14)), n, r, 0.28, p.ceramic, {
    segments: 22,
  });
  const facing = n[2] >= 0 ? n : scale(n, -1);
  if (Number.isFinite(params.farads)) {
    sb.label({
      text: formatFarads(params.farads),
      center: add(centre, scale(facing, 0.14 + INK)),
      ...facingLabel(facing),
      height: 0.34,
      maxWidth: 1.2,
      color: p.ceramicText,
    });
  }
}

/** A radial electrolytic: a can on short legs with the printed stripe down
    the side of its NEGATIVE lead (pin 2) and its value on top. */
function buildElectrolytic(ctx) {
  const { sb, u, n, m, p, params } = ctx;
  const r = 0.88;
  const Y0 = 0.3;
  const H = 2.3;
  standingLeads(ctx, 0.1, Y0, 0.35);
  sb.mesh.cylinder([m[0], Y0, m[2]], UP, r, H, p.capCan, {
    segments: 24,
    capColor: p.chipLeg,
  });
  sb.mesh.orientedBox(
    add([m[0], Y0 + H / 2, m[2]], scale(u, r - 0.02)),
    n,
    UP,
    u,
    [0.26, H / 2 - 0.05, 0.04],
    p.capStripe,
  );
  if (Number.isFinite(params.farads)) {
    sb.label({
      text: formatFarads(params.farads),
      center: [m[0], Y0 + H + INK, m[2]],
      height: 0.42,
      maxWidth: 1.5,
      color: p.chipBody,
    });
  }
}

// ── The bench bricks ───────────────────────────────────────────────────────

/** A brick's binding post, the colour of its terminal. */
function bindingPost(sb, x, z, color) {
  const top = DESK_Y + BRICK_HEIGHT;
  sb.mesh.cylinder([x, top, z], UP, 0.55, 0.3, sb.palette.partInset, {
    segments: 16,
  });
  sb.mesh.cylinder([x, top + 0.3, z], UP, 0.36, POST_HEIGHT - 0.3, color, {
    segments: 16,
  });
}

/** The bench power supply: a box on the desk beside the boards, its voltage
    on top and a red and a black binding post. */
function buildPsu(sb, doc, comp, def, params) {
  return buildBrick(sb, comp, def, `${params.volts} V`, (t) =>
    t.id === "+" ? sb.palette.wire.red : sb.palette.wire.black,
  );
}

/** The clock source: the same box with its rate on top, an orange `out`, a
    red `vcc` and a black `gnd` (the desk's pads), and the lamp that blinks
    with its output. */
function buildClock(sb, doc, comp, def, params) {
  const { wire } = sb.palette;
  const post = { out: wire.orange, vcc: wire.red, gnd: wire.black };
  const drawn = buildBrick(
    sb,
    comp,
    def,
    params.hz === "manual" ? "MAN" : hzLabel(params.hz), // the desk's badge
    (t) => post[t.id] ?? wire.black,
  );
  if (!drawn) return false;
  const p = sb.palette;
  const centre = [comp.x + 1.4, DESK_Y + BRICK_HEIGHT, comp.y + 1.4];
  sb.lamp({
    kind: "clock",
    compId: comp.id,
    on: p.simHigh,
    off: p.partInset,
    halo: { center: [centre[0], centre[1] + 0.2, centre[2]], radius: 1.05 },
  }).dome(centre, UP, 0.42, p.partInset, { segments: 16, rings: 5 });
  return true;
}

/** The electronic load: the brick in its own colour, its setting on top,
    a red `+` and a black `−` post. */
function buildLoad(sb, doc, comp, def, params) {
  return buildBrick(
    sb,
    comp,
    def,
    loadBadge(params),
    (t) => (t.id === "pos" ? sb.palette.wire.red : sb.palette.wire.black),
    { body: sb.palette.loadBody },
  );
}

/** The multimeter: its orange boot, the dark face on it, the LCD and the
    knob — its reading is the desk's, not drawn here; its probes and leads
    are not modelled. */
function buildMeter(sb, doc, comp, _def) {
  if (!Number.isFinite(comp.x) || !Number.isFinite(comp.y)) return false;
  const p = sb.palette;
  const { x, y } = comp;
  const slab = (x0, y0, x1, y1, r, from, rise, color) =>
    sb.mesh.extrude(roundedRect(x + x0, y + y0, x + x1, y + y1, r, from), [0, rise, 0], { top: shade(color, 1.12), side: color }); // prettier-ignore
  const face = DESK_Y + BRICK_HEIGHT;
  // Each look its boot: the Volt Hippo's orange, the EEVBlog look's blue.
  const bm235 = comp.params?.skin === "bm235";
  const boot = bm235 ? p.meterBm235Holster : p.meterHolster;
  if (isCompact(comp.params)) {
    // Folded down (components/meter-view.js `buildCompact`): its boot, face
    // and LCD, from the full body's frame less its origin.
    const { origin, width: w, height: h } = METER_COMPACT;
    slab(0, 0, w, h, 2.2, DESK_Y, BRICK_HEIGHT, boot);
    slab(1.6, 1.6, w - 1.6, h - 1.6, 1.2, face, 0.2, p.meterBm235Face);
    slab(5.7 - origin.x, 7.95 - origin.y, 26.3 - origin.x, 16.95 - origin.y, 0.2, face + 0.2, 0.05, p.meterBm235Lcd); // prettier-ignore
  } else {
    // The full body (components/meter-view.js `buildBody`): charcoal face,
    // LCD and knob where the BM235's photograph has them.
    // The boot and face waisted as the desk draws them (model/meter-probes.js
    // `bodyOutline`, a fan from its first point fills it).
    const waisted = (shape, from, rise, color) =>
      sb.mesh.extrude(bodyOutline(shape, { corner: 3, side: 12 }).map(([u, v]) => [x + u, from, y + v]), [0, rise, 0], { top: shade(color, 1.12), side: color }); // prettier-ignore
    waisted(METER_BODY.boot, DESK_Y, BRICK_HEIGHT, boot);
    waisted(METER_BODY.face, face, 0.2, p.meterBm235Face);
    slab(5.7, 7.95, 26.3, 19.95, 0.2, face + 0.2, 0.05, p.meterBm235Lcd);
    slab(8.6, 28.5, 23.2, 43.1, 7.2, face + 0.2, 0.9, p.meterBm235Knob);
  }
  for (const which of PROBES) buildProbe(sb, doc, comp, which);
  return true;
}

/** A probe's dimensions (pitch): its needle, its finger guard, its body —
    the desk's (components/meter-probe-layer.js) — and how steeply it is
    held off the board, degrees. */
const PROBE_3D = Object.freeze({ needle: 1.3, guard: 0.3, body: 6, radius: 0.42, guardRadius: 0.75, needleRadius: 0.08, rise: 40 }); // prettier-ignore

/**
 * One of a meter's probes and its lead: the tip on the hole or terminal it
 * touches (or on the desk where it lies), the probe held up along the line
 * back towards its jack, and the lead dropping to the desk from its end and
 * running into the jack on the meter's face, the plug standing in it.
 */
function buildProbe(sb, doc, meter, which) {
  const p = sb.palette;
  const color = namedColor(p, which === "red" ? "red" : "black");
  const tip2 = probeWorld(doc, meter, which);
  const end = tip2.at ? endOf(doc, tip2.at) : null;
  const tip = end?.p ?? [tip2.x, DESK_Y, tip2.y];
  const jack2 = jackWorld(meter, jackOf(meter.params, which));
  const face = DESK_Y + BRICK_HEIGHT + 0.2;
  const toward = [jack2.x - tip[0], 0, jack2.y - tip[2]];
  const flat = Math.hypot(toward[0], toward[2]) > 1e-6 ? normalize(toward) : [0, 0, 1]; // prettier-ignore
  const rad = (PROBE_3D.rise * Math.PI) / 180;
  const axis = normalize(add(scale(flat, Math.cos(rad)), [0, Math.sin(rad), 0])); // prettier-ignore
  const along = (d) => add(tip, scale(axis, d));
  sb.mesh.cylinder(tip, axis, PROBE_3D.needleRadius, PROBE_3D.needle, p.meterProbeMetal); // prettier-ignore
  sb.mesh.cylinder(along(PROBE_3D.needle), axis, PROBE_3D.guardRadius, PROBE_3D.guard, color); // prettier-ignore
  const bodyAt = PROBE_3D.needle + PROBE_3D.guard;
  sb.mesh.cylinder(along(bodyAt), axis, PROBE_3D.radius, PROBE_3D.body, color); // prettier-ignore
  const back = along(bodyAt + PROBE_3D.body);
  const jack = [jack2.x, face, jack2.y];
  const low = DESK_Y + WIRE_RADIUS * 1.5;
  // Along the desk round the meter, never under it: to its near side (or
  // straight), along below its foot, then up over the edge into the jack —
  // the body as it is drawn, compact or full.
  const { width, height } = meterSize(meter.params);
  const foot = meter.y + height + 2.5;
  const side = back[0] < meter.x ? meter.x - 2.5 : back[0] > meter.x + width ? meter.x + width + 2.5 : back[0]; // prettier-ignore
  const corner = [side, low, foot];
  const under = [jack[0], low, foot];
  const path = [
    ...cubicPoints(back, [back[0] + flat[0] * 4, low, back[2] + flat[2] * 4], [side, low, foot - 6], corner, 24), // prettier-ignore
    under,
    ...cubicPoints(under, [under[0], face + 2.4, foot], [jack[0], face + 2.4, jack[2] + 2], [jack[0], face + 1.2, jack[2]], 12), // prettier-ignore
  ];
  sb.mesh.tube(path, WIRE_RADIUS * 1.4, color, { sides: 7 });
  sb.mesh.cylinder(jack, [0, 1, 0], 0.55, 1.2, color);
}

/** Mains: the brick in its own colour, its output on top, the line's
    brown post and the neutral's blue. */
function buildAc(sb, doc, comp, def, params) {
  const { volts, hz } = acBadge(params);
  const p = sb.palette;
  return buildBrick(sb, comp, def, `${volts} ${hz}`, (t) => (t.id === "L" ? p.acLine : p.acNeutral), { body: p.acBody }); // prettier-ignore
}

/** The AC adapter: a wall transformer's black brick, its output and
    frequency on top, its two output posts grey as a transformer's. */
function buildAdapter(sb, doc, comp, def, params) {
  const p = sb.palette;
  const { volts, hz } = adapterBadge(params);
  return buildBrick(sb, comp, def, `${volts} ${hz}`, () => p.transformerCore, { body: p.adapterBody }); // prettier-ignore
}

/** The transformer: the brick in its own colour, its rating on top, four
    grey posts. */
function buildTransformer(sb, doc, comp, def, params) {
  const p = sb.palette;
  return buildBrick(sb, comp, def, transformerBadge(params), () => p.transformerCore, { body: p.transformerBody }); // prettier-ignore
}

function buildBrick(sb, comp, def, text, postColor, { body } = {}) {
  if (!Number.isFinite(comp.x) || !Number.isFinite(comp.y)) return false;
  const p = sb.palette;
  const { width, height } = def.size;
  const top = DESK_Y + BRICK_HEIGHT;
  sb.mesh.extrude(
    roundedRect(comp.x, comp.y, comp.x + width, comp.y + height, 0.5, DESK_Y),
    [0, BRICK_HEIGHT, 0],
    { top: shade(body ?? p.psuBody, 1.12), side: body ?? p.psuBody },
  );
  for (const t of def.terminals) {
    bindingPost(sb, comp.x + t.dx, comp.y + t.dy, postColor(t));
  }
  sb.label({
    text,
    center: [comp.x + width / 2, top + INK, comp.y + 1.6],
    height: 1.2,
    maxWidth: width - 3.4,
    color: p.psuBadge,
  });
  return true;
}

const BUILDERS = Object.freeze({
  chip: buildChip,
  "dip-switch": buildDipSwitch,
  digit: buildDigit,
  bar: buildBar,
  "bar-array": buildBarArray,
  "seven-seg": buildSevenSeg,
  lcd: buildLcd,
  slide: buildSlide,
  button: buildButton,
  tact: buildTact,
  pushlock: buildPushLock,
  bridge: buildBridge,
  rnet: buildRnet,
  pot: buildPot,
  transistor: buildTransistor,
  can: buildCan,
  span: buildSpan,
  psu: buildPsu,
  clock: buildClock,
  load: buildLoad,
  meter: buildMeter,
  ac: buildAc,
  transformer: buildTransformer,
  adapter: buildAdapter,
  regulator: buildRegulator,
  relay: buildRelay,
  speaker: buildSpeaker,
});

/** Every model kind there is — a test holds BUILDERS and modelKind to it. */
export const MODEL_KINDS = Object.freeze(Object.keys(BUILDERS));
